import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../../styles/components/FishTab.css";
import { SPECIES_META } from "../../../../utils/speciesMeta";

function FishIcon({ id, alt }) {
  const [hasIconError, setHasIconError] = useState(false);

  if (hasIconError || !id) {
    return null;
  }

  return (
    <img
      src={`/icons/${id.toLowerCase().replace(/_/g, "-")}-icon.png`}
      alt={alt}
      className="fish-icon"
      onError={() => setHasIconError(true)}
    />
  );
}

export default function FishTab({ data, onPickFish }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all"); // all | predators | nonpredators
  const [contentSpecies, setContentSpecies] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState(false);
  const isBasin = !!data?.isBasin;
  const contentRef = !isBasin ? data?.speciesContentRef ?? null : null;

  useEffect(() => {
    if (isBasin || !contentRef?.jsonPath) {
      setContentSpecies(null);
      setContentLoading(false);
      setContentError(false);
      return;
    }

    let cancelled = false;

    async function fetchSpeciesContent() {
      setContentSpecies(null);
      setContentLoading(true);
      setContentError(false);

      try {
        const response = await fetch(contentRef.jsonPath);

        if (!response.ok) {
          throw new Error(`Failed loading species JSON: ${response.status}`);
        }

        const contentJson = await response.json();
        const species = Array.isArray(contentJson?.species)
          ? contentJson.species
          : [];

        if (!cancelled) {
          setContentSpecies(species);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setContentSpecies([]);
          setContentError(true);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    }

    fetchSpeciesContent();

    return () => {
      cancelled = true;
    };
  }, [contentRef?.jsonPath, isBasin]);

  const rawFish = useMemo(() => {
    if (!isBasin) {
      return Array.isArray(contentSpecies) ? contentSpecies : [];
    }

    return data?.fish || [];
  }, [contentSpecies, data?.fish, isBasin]);

  const showNonBasinLoading =
    !isBasin &&
    !!contentRef?.jsonPath &&
    (contentLoading || contentSpecies === null);

  // Normalize fish data so basin (string IDs) and backend (objects) both work
  const fishList = rawFish.map((f) => {
  if (typeof f === "string") {
    const meta = SPECIES_META[f] || {};
    return {
      id: f,
      name: f.replace(/_/g, " "),
      is_predator: !!meta.is_predator,
      image: null,
    };
  }

  const speciesId = f?.id || "";
  const fallbackMeta = SPECIES_META[speciesId] || {};

  return {
    ...f,
    id: speciesId,
    name:
      f?.name ||
      f?.commonName ||
      f?.common_name ||
      speciesId.replace(/[_-]/g, " "),
    is_predator:
      typeof f?.is_predator === "boolean"
        ? f.is_predator
        : typeof f?.isPredator === "boolean"
          ? f.isPredator
          : !!fallbackMeta.is_predator,
    image: f?.image ?? null,
  };
});


  const predators = fishList.filter((f) => f.is_predator);
  const others = fishList.filter((f) => !f.is_predator);

  const envLabel =
    data?.environment === "marine"
      ? t("environment_marine", { defaultValue: "🌊 Marine" })
      : t("environment_freshwater", { defaultValue: "🦆 Freshwater" });

  const locLabel =
    data?.country && data.country !== "GLOBAL" ? ` • ${data.country}` : "";

  const renderList = (list) => (
  <ul className="fish-list">
    {list.map((f, i) => (
      <li key={i} className="fish-list-item">
        <FishIcon id={f.id} alt={t(`species.${f.id}`, { defaultValue: f.name })} />
        <button onClick={() => onPickFish(f)} className="fish-button">
          {t(`species.${f.id}`, { defaultValue: f.name })}
        </button>
      </li>
    ))}
  </ul>
);


  const visiblePredators = filter === "all" || filter === "predators";
  const visibleOthers = filter === "all" || filter === "nonpredators";
  const showNonBasinEmptyState =
    !isBasin &&
    !showNonBasinLoading &&
    (contentError || fishList.length === 0);

  return (
    <div className="tab-content">
      <div className="fish-header">
        <strong>
          {envLabel}
          {locLabel}
        </strong>
      </div>

      {/* Filters */}
      <div className="fish-filters">
        <button
          className={`fish-filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          {t("fish_filter_all", { defaultValue: "All" })}
        </button>
        <button
          className={`fish-filter-btn ${filter === "predators" ? "active" : ""}`}
          onClick={() => setFilter("predators")}
        >
          {t("fish_filter_predators", { defaultValue: "Predators" })}
        </button>
        <button
          className={`fish-filter-btn ${filter === "nonpredators" ? "active" : ""}`}
          onClick={() => setFilter("nonpredators")}
        >
          {t("fish_filter_nonpredators", { defaultValue: "Non-predators" })}
        </button>
      </div>

      {showNonBasinLoading && (
        <p className="fish-empty">
          {t("common_loading", { defaultValue: "Loading..." })}
        </p>
      )}

      {showNonBasinEmptyState && (
        <div className="fish-empty">
          <p>{t("fish.no_info", { defaultValue: "No species data available." })}</p>
          <p>{t("common_coming_soon", { defaultValue: "Coming soon" })}</p>
        </div>
      )}

      {isBasin && fishList.length === 0 && (
        <p className="fish-empty">
          {t("fish.no_info", { defaultValue: "No species data available." })}
        </p>
      )}

      {visiblePredators && predators.length > 0 && (
        <>
          <h4 className="fish-category-title">
            🦈 {t("species_category_predators", { defaultValue: "Predators" })}
          </h4>
          {renderList(predators)}
        </>
      )}

      {visibleOthers && others.length > 0 && (
        <>
          <h4 className="fish-category-title">
            🐠{" "}
            {t("species_category_non_predators", {
              defaultValue: "Forage & Others",
            })}
          </h4>
          {renderList(others)}
        </>
      )}
    </div>
  );
}
